import { Classes, Dialog } from "@blueprintjs/core";
import { FieldGroup } from "nsitools-react";
import * as React from "react";
import styled from "styled-components";
import * as Yup from "yup";

import {
  SiegeSocialFichierApi,
  SiegeSocialFichierUploadDto,
  SiegeSocialFichierUploadDtoFromJSON
} from "../../../../../api";
import { FGWalterFileInput, FGWalterSelectInput, SmallFormGenerator } from "../../../../../components";
import { useApiService, useCrudApi, useTl } from "../../../../../hooks";
import { useReferential } from "../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../locales";

const StyledDialog = styled(Dialog)`
  width: 600px;
  height: auto;
  background-color: white;
`;

export interface ISiegeSocialDocumentUploadDialogProps {
  idsiegeSocial: number;
  dialogOpen: boolean;
  onClose: (refresh?: boolean) => void;
}

export const SiegeSocialDocumentUploadDialog: React.FunctionComponent<ISiegeSocialDocumentUploadDialogProps> = ({
  idsiegeSocial,
  dialogOpen,
  onClose
}) => {
  const { t } = useTl();
  const api = useApiService(SiegeSocialFichierApi);

  const { data, loading, saveItem, saving, validationErrors } = useCrudApi(
    React.useMemo(
      () => ({
        getApiFn: () => {
          return SiegeSocialFichierUploadDtoFromJSON({ idsiegeSocial });
        },
        saveApiFn: async (d: SiegeSocialFichierUploadDto) => {
          return api.siegeSocialFichierUploadSiegeSocialFichier({ SiegeSocialFichierUploadDto: d });
        },
        onSaved: () => onClose(true)
      }),
      [api, idsiegeSocial, onClose]
    )
  );

  const FormSchema = React.useMemo(
    () =>
      Yup.object().shape({
        idtypeSiegeSocialFichier: Yup.number()
          .nullable()
          .required(t(ETLCodes.Required)),
        file: Yup.object()
          .nullable()
          .shape({
            fileName: Yup.string()
              .nullable()
              .required(t(ETLCodes.Required)),
            contentB64: Yup.string()
              .nullable()
              .required(t(ETLCodes.Required))
          })
      }),
    [t]
  );

  const [typesSiegeSocialFichier, tLoading] = useReferential(a => a.referentialGetTypesSiegeSocialFichier());

  return (
    <StyledDialog title={t(ETLCodes.UploadFichier)} isOpen={dialogOpen} onClose={() => onClose(false)}>
      <div className={Classes.DIALOG_BODY}>
        <SmallFormGenerator
          initialValues={data}
          onSubmit={saveItem}
          editMode={true}
          validationSchema={FormSchema}
          onCancel={() => onClose(false)}
          showDeleteButton={false}
          saving={saving}
          loading={loading}
          validationErrors={validationErrors}
        >
          <FieldGroup>
            <FGWalterFileInput name="file" label={t(ETLCodes.Fichier)} requiredMark />
            <FGWalterSelectInput
              name="idtypeSiegeSocialFichier"
              label={t(ETLCodes.Type)}
              items={typesSiegeSocialFichier}
              loading={tLoading}
            />
          </FieldGroup>
        </SmallFormGenerator>
      </div>
    </StyledDialog>
  );
};
